import axios from 'axios';
import apiService from './ApiService';

export interface OAuthConnection {
  id: string;
  clientId: string;
  clientName: string;
  scopes: string[];
  status: 'active' | 'inactive' | 'revoked';
  lastUsedAt: string | null;
  lastRefreshedAt: string | null;
  createdAt: string;
}

export interface ConsentInfo {
  client_id: string;
  client_name: string;
  client_uri: string | null;
  logo_uri: string | null;
  // SNAP-2310: false for self-registered clients that no one has reviewed.
  trusted?: boolean;
  // SNAP-2359: lifetimes in seconds, from the backend. refresh_token_lifetime_max is null while the
  // absolute cap is disabled. An empty scope_ceiling means no ceiling (legacy clients only).
  access_token_lifetime?: number;
  refresh_token_lifetime?: number;
  refresh_token_lifetime_max?: number | null;
  scope_ceiling?: string[];
}

class OAuthConnectionService {
  async list(companyId?: string): Promise<{ data: OAuthConnection[] }> {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const response = await axios.get(`${apiService.baseUrl}/oauth-connections${params}`, apiService.getRequestConfig());
    return response.data;
  }

  async disconnect(connectionId: string): Promise<{ data: OAuthConnection }> {
    const response = await axios.delete(
      `${apiService.baseUrl}/oauth-connections/${connectionId}`,
      apiService.getRequestConfig()
    );
    return response.data;
  }

  async getConsentInfo(clientId: string): Promise<ConsentInfo> {
    const response = await axios.get(
      `${apiService.baseUrl}/oauth/consent-info?client_id=${encodeURIComponent(clientId)}`,
      apiService.getRequestConfig()
    );
    return response.data;
  }

  async approveConsent(params: {
    client_id: string;
    redirect_uri: string;
    scope: string;
    state: string;
    code_challenge: string;
    code_challenge_method: string;
    company_ids?: string[];
  }): Promise<{ redirect_url: string }> {
    const response = await axios.post(
      `${apiService.baseUrl}/oauth/authorize/decide`,
      { ...params, approved: true },
      apiService.getRequestConfig()
    );
    return response.data;
  }

  async denyConsent(params: {
    client_id: string;
    redirect_uri: string;
    state: string;
  }): Promise<{ redirect_url: string }> {
    const response = await axios.post(
      `${apiService.baseUrl}/oauth/authorize/decide`,
      { ...params, approved: false },
      apiService.getRequestConfig()
    );
    return response.data;
  }
}

export default new OAuthConnectionService();
